/**
 * 지구를 도는 달 — 운석 진로를 방해하는 장애물.
 *
 * 시각적 의미: 지구는 살아있는 행성이라 자연스럽게 달이 따라다녀야 한다.
 * 게임적 의미: 발사한 운석이 달과 충돌하면 튕겨나가, 단순히 똑바로 쏘는
 * 것 만으로는 안 되고 달의 위치를 보고 타이밍을 맞춰야 한다.
 *
 * 구현:
 *   - 시각: 작은 cel-shaded 은빛 구체 + 흑외곽선
 *   - 물리: cannon-es kinematic body — 외력에 영향 없이 매 프레임 직접
 *     position 만 update. 운석은 cannon 의 일반 충돌 처리로 자연스럽게
 *     튕긴다.
 *   - 궤도: Earth 중심 기준 고정 반경의 원형 궤도, z=0 평면, 약 22초
 *     주기로 천천히 공전.
 *
 * 주의: 이 달은 sim.planets 에 들어가지 않으므로 atmosphere breach / escape /
 * merge tier 사다리 어디에도 영향 없음. 별도 body 로만 존재.
 */

import { Body, Sphere as CannonSphere } from "cannon-es";
import {
  Group,
  Mesh,
  MeshToonMaterial,
  SphereGeometry,
} from "three";

import { createToonOutline } from "../render/toon-outline.ts";
import { createToonGradient } from "../shaders/toon-gradient.ts";
import { createMoonPhaseShell } from "../shaders/moon-phase.ts";
import { paintMoonSurface } from "./moon-surface-paint.ts";

const MOON_COLOR = 0xc0bcd0;
const MOON_RADIUS = 1.0;
const MOON_OUTLINE_SCALE = 1.06;
const ORBIT_RADIUS = 13;
// Slow orbit — one full revolution in 50s. Leisurely phase cycle that
// gives the player time to notice the moon's position and time their
// shots, while still completing a few cycles per run.
const ORBIT_PERIOD_SEC = 50;
const SHARED_GRADIENT = createToonGradient(4);

export interface OrbitingMoonOptions {
  /** 안커 — 달이 도는 중심점 (지구 위치). */
  center: { x: number; y: number; z: number };
  /** 시작 각도 (라디안). 0 이면 +x 방향에서 시작. */
  initialAngle?: number;
}

export interface OrbitingMoon {
  group: Group;
  body: Body;
  /** 매 프레임 호출 — 새 궤도 각도 계산하고 mesh+body 위치 갱신. */
  tick(dt: number): void;
  detach(): void;
}

export function createOrbitingMoon(opts: OrbitingMoonOptions): OrbitingMoon {
  const center = opts.center;
  let angle = opts.initialAngle ?? 0;
  // 초당 회전량 = 2π / 주기. 음수면 반대 방향.
  const angularSpeed = (Math.PI * 2) / ORBIT_PERIOD_SEC;

  const group = new Group();
  group.name = "orbiting-moon";

  const geometry = new SphereGeometry(MOON_RADIUS, 32, 24);
  const surfaceTexture = paintMoonSurface();
  const material = new MeshToonMaterial({
    color: MOON_COLOR,
    gradientMap: SHARED_GRADIENT,
    map: surfaceTexture,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = "moon-surface";

  // Cel outline — same trick as Earth + meteors.
  const outline = createToonOutline({
    planetRadius: MOON_RADIUS,
    scale: MOON_OUTLINE_SCALE,
  });
  mesh.add(outline);

  // Phase shadow shell — cartoon "half/crescent moon" effect that
  // cycles as the moon orbits. Slightly larger than the surface mesh
  // so the shadow visibly hugs the silhouette.
  const phaseShell = createMoonPhaseShell({ radius: MOON_RADIUS * 1.01 });
  mesh.add(phaseShell.mesh);

  group.add(mesh);

  // Kinematic body — meteors collide with it but it ignores forces.
  // Match the visible outline radius so contact happens at the rim.
  const bodyRadius = MOON_RADIUS * MOON_OUTLINE_SCALE;
  const body = new Body({
    mass: 0,
    type: Body.KINEMATIC,
    shape: new CannonSphere(bodyRadius),
  });
  const startX = center.x + Math.cos(angle) * ORBIT_RADIUS;
  const startY = center.y + Math.sin(angle) * ORBIT_RADIUS;
  body.position.set(startX, startY, center.z);
  group.position.set(startX, startY, center.z);

  const tick = (dt: number): void => {
    angle += angularSpeed * dt;
    if (angle > Math.PI * 2) angle -= Math.PI * 2;
    const x = center.x + Math.cos(angle) * ORBIT_RADIUS;
    const y = center.y + Math.sin(angle) * ORBIT_RADIUS;
    body.position.set(x, y, center.z);
    group.position.set(x, y, center.z);
    // Very gentle self-rotation so crater detail rotates visibly
    // without competing with the orbit period.
    mesh.rotation.y += dt * 0.08;
    // Drive the cartoon moon-phase shadow off the orbit angle.
    phaseShell.setPhaseAngle(angle);
  };

  const detach = (): void => {
    geometry.dispose();
    material.dispose();
    surfaceTexture.dispose();
    (outline.geometry as SphereGeometry).dispose();
    (outline.material as { dispose(): void }).dispose();
    phaseShell.detach();
    group.removeFromParent();
  };

  return { group, body, tick, detach };
}

export const __testing = {
  MOON_COLOR,
  MOON_RADIUS,
  ORBIT_RADIUS,
  ORBIT_PERIOD_SEC,
};
